import {
  SIconWrapper,
  SOption,
  SOptionsList,
  SSelectCountryPicker,
  SFlagImg,
  SLabel,
  SSearchInput,
  SSelectCountryPanel,
  SEmptyState,
} from './SelectCountry.styled'
import {
  ButtonHTMLAttributes,
  forwardRef,
  ReactElement,
  RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Text } from 'components/Typography'
import { getCountryCallingCode } from 'react-phone-number-input'
import { Icon } from 'components/icon'

type Option = {
  value?: string | undefined
  label: string
}

interface Props {
  onChange: (newValue: string | undefined) => void
  onClick?: () => void
  options?: Option[]
  searchValue?: string
  onSearchChange?: (value: string) => void
  inputRef?: RefObject<HTMLInputElement>
  selectedValue?: string
  listboxId?: string
  onEscape?: () => void
  onTabOut?: () => void
}

function Option({
  label,
  value,
  onChange,
  onClick,
  optionId,
  highlighted,
  selected,
  onMouseEnter,
  optionRef,
}: Option & {
  onChange: (newValue: string | undefined) => void
  onClick?: () => void
  optionId: string
  highlighted: boolean
  selected: boolean
  onMouseEnter: () => void
  optionRef: (node: HTMLDivElement | null) => void
}) {
  if (!value) {
    return null
  }

  return (
    <SOption
      ref={optionRef}
      id={optionId}
      role="option"
      tabIndex={-1}
      aria-selected={selected}
      highlighted={highlighted}
      data-country={value}
      onMouseEnter={onMouseEnter}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        onClick?.()
        onChange(value)
      }}
    >
      <SFlagImg src={`/flags/${value}.svg`} />

      <SLabel>{label}</SLabel>
      <Text>{`+${getCountryCallingCode(value)}`}</Text>
    </SOption>
  )
}

export const SelectCountryPicker = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    flag?: ReactElement
    isOpen?: boolean
  }
>(({ flag, isOpen, 'aria-label': ariaLabel, ...props }, ref) => {
  return (
    <SSelectCountryPicker
      type="button"
      ref={ref}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-label={ariaLabel ?? 'Select country calling code'}
      data-open={isOpen ? 'true' : 'false'}
      {...props}
    >
      <SIconWrapper>{flag || <Icon name="Globe" />}</SIconWrapper>
      <Icon name="CaretDown" css={{ marginLeft: '.5rem' }} />
    </SSelectCountryPicker>
  )
})
SelectCountryPicker.displayName = 'SelectCountryPicker'

export function SelectCountry({
  onChange,
  options,
  onClick,
  searchValue,
  onSearchChange,
  inputRef,
  selectedValue,
  listboxId: providedListboxId,
  onEscape,
  onTabOut,
}: Props) {
  const generatedListboxId = useId()
  const listboxId = providedListboxId ?? generatedListboxId
  const optionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const visibleOptionValues = useMemo(
    () => options?.map((option) => option.value).filter((value): value is string => !!value) ?? [],
    [options]
  )
  const [highlightedValue, setHighlightedValue] = useState<string | undefined>()

  useEffect(() => {
    setHighlightedValue((currentValue) => {
      if (currentValue && visibleOptionValues.includes(currentValue)) {
        return currentValue
      }

      if (selectedValue && visibleOptionValues.includes(selectedValue)) {
        return selectedValue
      }

      if (searchValue && visibleOptionValues.length > 0) {
        return visibleOptionValues[0]
      }

      return undefined
    })
  }, [searchValue, selectedValue, visibleOptionValues])

  useEffect(() => {
    if (!highlightedValue) return

    optionRefs.current[highlightedValue]?.scrollIntoView?.({
      block: 'nearest',
    })
  }, [highlightedValue])

  function moveHighlight(direction: 1 | -1) {
    if (!visibleOptionValues.length) return

    setHighlightedValue((currentValue) => {
      const currentIndex = currentValue ? visibleOptionValues.indexOf(currentValue) : -1

      if (currentIndex === -1) {
        return direction === 1
          ? visibleOptionValues[0]
          : visibleOptionValues[visibleOptionValues.length - 1]
      }

      const nextIndex = Math.min(
        visibleOptionValues.length - 1,
        Math.max(0, currentIndex + direction)
      )
      return visibleOptionValues[nextIndex]
    })
  }

  if (!options) {
    return null
  }

  return (
    <SSelectCountryPanel>
      <SSearchInput
        ref={inputRef}
        autoFocus
        type="search"
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-label="Search country or calling code"
        aria-expanded
        aria-haspopup="listbox"
        aria-activedescendant={highlightedValue ? `${listboxId}-${highlightedValue}` : undefined}
        aria-controls={listboxId}
        placeholder="Search country or code..."
        value={searchValue ?? ''}
        onChange={(e) => onSearchChange?.(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            moveHighlight(1)
            return
          }

          if (e.key === 'ArrowUp') {
            e.preventDefault()
            moveHighlight(-1)
            return
          }

          if (e.key === 'Enter' && highlightedValue) {
            e.preventDefault()
            onChange(highlightedValue)
            return
          }

          if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            onEscape?.()
            return
          }

          if (e.key === 'Tab') {
            window.setTimeout(() => {
              onTabOut?.()
            }, 0)
          }
        }}
      />
      {options.length ? (
        <SOptionsList id={listboxId} role="listbox" aria-label="Country calling codes">
          {options.map((option, i) => (
            <Option
              onClick={onClick}
              key={option.value ?? i}
              {...option}
              optionId={`${listboxId}-${option.value}`}
              highlighted={option.value === highlightedValue}
              selected={option.value === selectedValue}
              onMouseEnter={() => option.value && setHighlightedValue(option.value)}
              optionRef={(node) => {
                if (!option.value) return
                optionRefs.current[option.value] = node
              }}
              onChange={onChange}
            />
          ))}
        </SOptionsList>
      ) : (
        <SEmptyState role="status">No countries found</SEmptyState>
      )}
    </SSelectCountryPanel>
  )
}
